import PubSub from "pubsub-js"
import { getDefaultProperties } from "../StyleClassEditor/data/property-definitions"
import {
  createCSSClassUpdate,
  createCSSPropertyUpdate,
  createCSSClassRenameUpdate,
  createCSSClassDeleteUpdate,
  publishSurgicalUpdates,
} from "../utils/surgicalUpdates"

/**
 * Centralized CSS Class Management Service
 * Single source of truth for all CSS class operations
 */
class CSSClassManager {
  constructor() {
    this.classes = new Map() // className -> classData
    this.subscribers = new Set()
    this.isInitialized = false
  }

  /**
   * Initialize with classes from HTML
   */
  initialize(classesData = []) {
    this.classes.clear()
    classesData.forEach(classData => {
      this.classes.set(classData.name, classData)
    })
    this.isInitialized = true
    this._notifySubscribers()
  }

  /**
   * Subscribe to class changes
   */
  subscribe(callback) {
    this.subscribers.add(callback)
    return () => this.subscribers.delete(callback)
  }

  /**
   * Get all classes as array
   */
  getAllClasses() {
    return Array.from(this.classes.values())
  }

  /**
   * Get a specific class
   */
  getClass(className) {
    return this.classes.get(className)
  }

  /**
   * Create a new class
   */
  createClass(className, properties = {}, skipDefaults = false) {
    if (!className?.trim()) {
      throw new Error("Class name is required")
    }

    const trimmedName = className.trim()
    const finalProperties = skipDefaults ? properties : { ...getDefaultProperties(), ...properties }

    const newClass = {
      id: trimmedName,
      name: trimmedName,
      type: 'CSS Class',
      properties: finalProperties
    }

    this.classes.set(trimmedName, newClass)

    // Send surgical update to HTML
    this._sendCSSUpdate(trimmedName, finalProperties, "create")

    this._notifySubscribers()
    return newClass
  }

  /**
   * Update class properties
   */
  updateClass(className, properties) {
    const existingClass = this.classes.get(className)
    if (!existingClass) {
      throw new Error(`Class ${className} not found`)
    }

    const updatedClass = {
      ...existingClass,
      properties
    }

    this.classes.set(className, updatedClass)

    // Send surgical update to HTML
    this._sendCSSUpdate(className, properties, "update")

    this._notifySubscribers()
    return updatedClass
  }

  /**
   * Update a single property
   */
  updateProperty(className, property, value) {
    const existingClass = this.classes.get(className)
    if (!existingClass) {
      throw new Error(`Class ${className} not found`)
    }

    const updatedProperties = {
      ...existingClass.properties,
      [property]: value
    }

    const updatedClass = {
      ...existingClass,
      properties: updatedProperties
    }

    this.classes.set(className, updatedClass)

    // Send efficient single property update instead of full class update
    this._sendCSSPropertyUpdate(className, property, value)

    this._notifySubscribers()
    return updatedClass
  }

  /**
   * Rename a class
   */
  renameClass(oldClassName, newClassName, properties = null) {
    const existingClass = this.classes.get(oldClassName)
    if (!existingClass) {
      throw new Error(`Class ${oldClassName} not found`)
    }

    const finalProperties = properties || existingClass.properties

    // Remove old class
    this.classes.delete(oldClassName)

    // Create new class
    const newClass = {
      ...existingClass,
      id: newClassName,
      name: newClassName,
      properties: finalProperties
    }

    this.classes.set(newClassName, newClass)

    // Send surgical rename update to HTML
    this._sendCSSRename(oldClassName, newClassName, finalProperties)

    this._notifySubscribers()
    return newClass
  }

  /**
   * Delete a class
   */
  deleteClass(className) {
    const existingClass = this.classes.get(className)
    if (!existingClass) {
      return false
    }

    this.classes.delete(className)

    // Send surgical delete update to HTML
    this._sendCSSDelete(className)

    this._notifySubscribers()
    return true
  }

  /**
   * Upsert class (update if exists, create if doesn't)
   */
  upsertClass(className, properties = {}, skipDefaults = false) {
    if (this.classes.has(className)) {
      return this.updateClass(className, skipDefaults ? properties : { ...getDefaultProperties(), ...properties })
    } else {
      return this.createClass(className, properties, skipDefaults)
    }
  }

  // Private methods

  _sendCSSUpdate(className, properties, operation) {
    try {
      const update = createCSSClassUpdate(className, properties)
      publishSurgicalUpdates(update, "css-update", PubSub)
    } catch (error) {
      console.error(`Error sending CSS ${operation} update:`, error)
    }
  }

  _sendCSSPropertyUpdate(className, property, value) {
    try {
      const update = createCSSPropertyUpdate(className, property, value)
      publishSurgicalUpdates(update, "css-update", PubSub)
    } catch (error) {
      console.error("Error sending CSS property update:", error)
    }
  }

  _sendCSSRename(oldClassName, newClassName, properties) {
    try {
      const update = createCSSClassRenameUpdate(oldClassName, newClassName, properties)
      publishSurgicalUpdates(update, "css-update", PubSub)
    } catch (error) {
      console.error("Error sending CSS rename update:", error)
    }
  }

  _sendCSSDelete(className) {
    try {
      const update = createCSSClassDeleteUpdate(className)
      publishSurgicalUpdates(update, "css-update", PubSub)
    } catch (error) {
      console.error("Error sending CSS delete update:", error)
    }
  }

  _notifySubscribers() {
    const classes = this.getAllClasses()
    this.subscribers.forEach(callback => {
      try {
        callback(classes)
      } catch (error) {
        console.error("Error notifying CSS class subscriber:", error)
      }
    })
  }
}

// Export singleton instance
export const cssClassManager = new CSSClassManager()
export default cssClassManager